/**
 * sticky-atc.js
 * ──────────────
 * Sticky Add-to-Cart bar for Ciclo Essentials product pages.
 *
 * Behaviour:
 * 1. IntersectionObserver watches the main ATC button (#ProductSubmitButton-<sectionId>).
 * 2. When the button scrolls OUT of the viewport → sticky bar slides up.
 * 3. When the button scrolls BACK in → sticky bar hides.
 * 4. Clicking the sticky button programmatically clicks the main form's submit button,
 *    so all of Dawn's product-form.js validation / cart logic runs normally.
 * 5. Variant changes (dispatched by Dawn's product-info.js as 'variant:change') are
 *    listened to and reflected in the sticky bar's price, variant label, and button state.
 *
 * No external deps. ~2.5 KB minified.
 */
(function () {
  'use strict';

  const BAR_ID = 'ciclo-sticky-atc';
  // Matches the store's money format as rendered by Liquid (e.g. "Rs. "), read on init.
  let currencyPrefix = 'Rs. ';

  function init() {
    const bar = document.getElementById(BAR_ID);
    if (!bar) return;

    const sectionId  = bar.dataset.sectionId;
    const sentinelId = bar.dataset.sentinelId;   // "ProductSubmitButton-<sectionId>"
    const sentinel   = document.getElementById(sentinelId);
    const stickyBtn  = document.getElementById('ciclo-satc-btn');
    const variantEl  = document.getElementById('ciclo-satc-variant');
    const priceEl    = document.getElementById('ciclo-satc-price');

    if (!sentinel || !stickyBtn) return;

    const initialPrice = priceEl && priceEl.querySelector('.ciclo-satc__price-current');
    const prefixMatch = initialPrice && /^[^\d]*/.exec(initialPrice.textContent.trim());
    if (prefixMatch && prefixMatch[0]) currencyPrefix = prefixMatch[0];

    // ── 1. Show / hide via IntersectionObserver ─────────────────────────────
    let observed = sentinel;
    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            // Main button visible → hide bar
            bar.classList.remove('ciclo-satc--visible');
            bar.setAttribute('aria-hidden', 'true');
          } else if (entry.boundingClientRect.top < 0) {
            // Main button scrolled above the viewport → show bar
            bar.classList.add('ciclo-satc--visible');
            bar.setAttribute('aria-hidden', 'false');
          }
        });
      },
      { threshold: 0, rootMargin: '0px' }
    );
    observer.observe(sentinel);

    // Dawn can re-render the product section (e.g. combined listings); keep watching the live button.
    function reobserve() {
      const current = document.getElementById(sentinelId);
      if (current && current !== observed) {
        observer.unobserve(observed);
        observer.observe(current);
        observed = current;
      }
    }

    // ── 2. Sticky button click → submit main form ───────────────────────────
    stickyBtn.addEventListener('click', function () {
      const mainBtn = document.getElementById(sentinelId);
      if (mainBtn && !mainBtn.disabled) {
        mainBtn.click();
      }
    });

    // ── 3. Sync variant state from Dawn's variant-change events ────────────
    // Dawn 15's product-info.js announces variant changes through pubsub:
    // publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId, html, variant } }).
    // variant is undefined when the chosen option combination doesn't exist.
    function onVariantChange(variant) {
        reobserve();

        if (!variant) {
          stickyBtn.disabled = true;
          stickyBtn.setAttribute('aria-disabled', 'true');
          stickyBtn.textContent = 'Unavailable';
          if (variantEl) variantEl.textContent = '';
          return;
        }

        // Update available state
        if (variant.available) {
          stickyBtn.disabled = false;
          stickyBtn.removeAttribute('aria-disabled');
          stickyBtn.textContent = 'Add to Cart';
        } else {
          stickyBtn.disabled = true;
          stickyBtn.setAttribute('aria-disabled', 'true');
          stickyBtn.textContent = 'Sold Out';
        }

        // Update variant label
        if (variantEl) {
          variantEl.textContent = variant.title !== 'Default Title' ? variant.title : '';
        }

        // Update price (only for non-dealer display — dealer pricing block manages its own)
        if (priceEl && !bar.querySelector('.ciclo-price')) {
          const priceSpan = priceEl.querySelector('.ciclo-satc__price-current');
          const cmpSpan   = priceEl.querySelector('.ciclo-satc__price-compare');

          if (priceSpan) {
            priceSpan.textContent = formatMoney(variant.price);
          }
          if (cmpSpan) {
            if (variant.compare_at_price > variant.price) {
              cmpSpan.textContent = formatMoney(variant.compare_at_price);
              cmpSpan.style.display = '';
            } else {
              cmpSpan.style.display = 'none';
            }
          }
        }
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        const data = event && event.data;
        if (!data || data.sectionId !== sectionId) return;
        onVariantChange(data.variant);
      });
    }
    const productInfo = document.getElementById('MainProduct-' + sectionId);
    if (productInfo) {
      productInfo.addEventListener('variant:change', function (evt) {
        onVariantChange(evt.detail && evt.detail.variant);
      });
    }
  }

  /**
   * Minimal money formatter matching Shopify's default INR format.
   * Shopify stores prices in paise (× 100).
   */
  function formatMoney(paise) {
    const rupees = paise / 100;
    return currencyPrefix + rupees.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  // Initialise after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
